import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { cn } from '@/lib/utils';

interface ServicePackage {
  id: string;
  name: string;
  for: string;
  bullets: string[];
  ctaLabel: string;
  ctaHref: string;
  isPro: boolean;
  hasScarcityLine: boolean;
}

const PACKAGES: ServicePackage[] = [
  {
    id: 'starter',
    name: 'Starter – Brand & Site Essentials',
    for: 'For solo consultants and small practices with one core offer.',
    bullets: [
      'Clarified positioning and simple brand direction.',
      'Clean, conversion-focused 1–3 page site.',
      'Booking and basic contact systems set up correctly.',
    ],
    ctaLabel: 'See Starter details →',
    ctaHref: '/services#starter',
    isPro: false,
    hasScarcityLine: false,
  },
  {
    id: 'basic',
    name: 'Basic – Brand + Website Build',
    for: 'For boutique agencies and experts with multiple services and higher pricing.',
    bullets: [
      'Brand system with visuals, messaging, and structure.',
      'Multi-page website designed to support multiple offers.',
      'Key automations for leads, intake, and follow-up.',
    ],
    ctaLabel: 'See Basic details →',
    ctaHref: '/services#basic',
    isPro: false,
    hasScarcityLine: false,
  },
  {
    id: 'pro',
    name: 'Pro – Full Launch Studio',
    for: 'For founders who want domain, brand, website, and ongoing advisory in one place.',
    bullets: [
      'Everything in Basic, plus deeper funnel and lead-gen support.',
      'Ongoing optimization and advisory for your first critical months.',
      'One partner responsible for how it all works together.',
    ],
    ctaLabel: 'See Pro details →',
    ctaHref: '/services#pro',
    isPro: true,
    hasScarcityLine: true,
  },
];

export function HomeServicesSnapshot() {
  const headingReveal = useRevealOnce({ amount: 0.25 });
  const cardsReveal = useRevealOnce({ amount: 0.25 });
  const guaranteeReveal = useRevealOnce({ amount: 0.25 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <section id="services-snapshot" className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={prefersReducedMotion ? {} : getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-slate-900"
            >
              When you're ready to launch, choose how much help you want.
            </motion.h2>
            <motion.p
              variants={prefersReducedMotion ? {} : getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed"
            >
              Start with your domain, then pick the level of support that fits your stage. Every engagement starts with a clear plan, not random design work.
            </motion.p>
          </motion.div>

          {/* Three package cards */}
          <motion.div
            ref={cardsReveal.ref}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16"
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={cardsReveal.controls}
          >
            {PACKAGES.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                variants={prefersReducedMotion ? {} : getGridItemVariants(index)}
                className="group h-full"
              >
                <Card
                  className={cn(
                    'h-full flex flex-col border-slate-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:border-[#0000FF]/50',
                    pkg.isPro && 'border-2 border-[#0000FF]/30 hover:border-[#0000FF]/50 hover:shadow-[#0000FF]/10'
                  )}
                >
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl md:text-2xl font-bold mb-3 text-slate-900">
                      {pkg.name}
                    </CardTitle>
                    <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-4">
                      {pkg.for}
                    </p>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col">
                    <ul className="space-y-3 mb-6">
                      {pkg.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex items-start gap-2 text-sm md:text-base text-slate-600">
                          <span className="text-[#0000FF] mt-1.5 shrink-0">•</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>

                    {pkg.hasScarcityLine && (
                      <p className="text-xs md:text-sm text-slate-500 italic mb-6 border-t border-slate-200 pt-4">
                        Limited to a few Pro launch builds at a time so I can actually partner with you.
                      </p>
                    )}

                    <div className="mt-auto pt-4">
                      <Link
                        to={pkg.ctaHref}
                        className="inline-flex items-center text-base md:text-lg font-semibold text-[#0000FF] hover:text-[#0000EE] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0000FF] focus-visible:ring-offset-2"
                      >
                        {pkg.ctaLabel}
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Brand-fit guarantee block */}
          <motion.div
            ref={guaranteeReveal.ref}
            className="mt-8 md:mt-12"
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={guaranteeReveal.controls}
          >
            <motion.div
              variants={prefersReducedMotion ? {} : getGridItemVariants(0)}
              className="rounded-xl border border-slate-200 bg-slate-50/50 p-6 md:p-8 text-center"
            >
              <h3 className="text-base md:text-lg font-bold mb-2 text-slate-900">
                Brand-fit guarantee
              </h3>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-3xl mx-auto">
                I refine your core brand identity until it feels right for you and your market—no extra design drama, no disappearing designer.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

